export const BUCKET_TYPE = {
  VOICE: 'VOICE',
  DATA: 'DATA',
  MESSAGE: 'MESSAGE',
  MONETARY: 'MONETARY',
  MIXED: 'mixed', // TODO: remove when types are unified
  TOP_CALLED: 'MIXED', // TODO: remove when types are unified
  PACKAGE: 'PACKAGE',
};

export const COUNTER_TYPE = {
  ORIGINAL_SIZE: 'originalSize',
  CARRY_OVER: 'carryOver',
  CONSUMED: 'consumed',
  CONSUMED_MONETARY: 'consumedMonetary',
  OVERCONSUMPTION: 'overConsumption',
  PHONE_NUMBER: 'phoneNumber',
};

export const COUNTER_UNIT = {
  VOICE: 'SECONDS',
  MESSAGE: 'PCS',
  DATA: 'kB',
  MONETARY: 'EUR',
  PACKAGE: 'PCS',
};

const CREDIT_ID = 'EUR';
const BONUS_CREDIT_ID = 'B_EUR_Bonus_post';
const PREVIOUS_VOICE_OSK_ID = 'DWH_VOICE_OSK';
const PREVIOUS_VOICE_NON_OSK_ID = 'DWH_VOICE_NON_OSK';
const PREVIOUS_SMS_ID = 'DWH_SMS';
const PREVIOUS_MMS_ID = 'DWH_MMS';
const PREVIOUS_DATA_ID = 'DWH_DATA';
const PREVIOUS_TOP_CALLED_1 = 'DWH_TOP_CALLED_1';
const PREVIOUS_TOP_CALLED_2 = 'DWH_TOP_CALLED_2';
const PREVIOUS_TOP_CALLED_3 = 'DWH_TOP_CALLED_3';
const PREVIOUS_CONSUMPTION_BUCKETS = [
  PREVIOUS_VOICE_OSK_ID,
  PREVIOUS_VOICE_NON_OSK_ID,
  PREVIOUS_SMS_ID,
  PREVIOUS_MMS_ID,
  PREVIOUS_DATA_ID,
  PREVIOUS_TOP_CALLED_1,
  PREVIOUS_TOP_CALLED_2,
  PREVIOUS_TOP_CALLED_3,
];

// Represents total and full remaining value in "infinite" packages
export const MAX_VALUE = 99999999999999;

/**
 * Model representing a consumption bucket - a pool of units available for use
 * with associated usage and other related information
 *
 * @property {object} _data - raw bucket data
 */
export default class Bucket {
  constructor(bucketData) {
    this._data = bucketData;
  }

  /**
   * @return {string}
   */
  get id() {
    if (!this._data.id) {
      throw new Error('Bucket ID not found.');
    }

    return this._data.id;
  }

  /**
   * @return {string}
   */
  get type() {
    if (!Object.values(BUCKET_TYPE).includes(this._data.usageType)) {
      throw new Error(`Unknown bucket type "${this._data.usageType}".`);
    }

    return this._data.usageType;
  }

  /**
   * The commercial name of the bucket, with the trailing colon removed
   *
   * @return {string}
   */
  get name() {
    if (!this._data.name) {
      throw new Error(`Missing name in bucket "${this.id}".`);
    }

    // 1st capturing group matches the entirety of the name excluding the trailing `:` and any following whitespaces
    const regexp = /^(.*?)(:?\s*$)/;

    return this._data.name.replace(regexp, '$1');
  }

  /**
   * @return {number}
   */
  get total() {
    return this._getSingleCounterByType(COUNTER_TYPE.ORIGINAL_SIZE).value;
  }

  /**
   * @return {number}
   */
  get used() {
    return this._getSingleCounterByType(COUNTER_TYPE.CONSUMED).value;
  }

  /**
   * @return {number}
   */
  get carryOver() {
    return this._getSingleCounterByType(COUNTER_TYPE.CARRY_OVER).value;
  }

  /**
   * @return {number}
   */
  get usedEur() {
    return this._getSingleCounterByType(COUNTER_TYPE.CONSUMED_MONETARY).value;
  }

  /**
   * @return {number}
   */
  get remaining() {
    const isOptional = this.isAirtime || this.isPreviousConsumption;
    const bucketBalance = this._getBucketBalanceObject(isOptional);

    if (isOptional && !bucketBalance) {
      return 0;
    }
    const { remainingValue } = bucketBalance;
    if (typeof remainingValue !== 'number') {
      if (!remainingValue) {
        throw new Error(`Missing bucket balance "remainingValue" in bucket "${this.id}"`);
      }
      throw new Error(
        `Unexpected bucket balance "remainingValue" in bucket "${this.id}": ${remainingValue}`,
      );
    }
    return remainingValue;
  }

  /**
   * @return {string}
   */
  get topCalledSn() {
    return this._getSingleCounterByType(COUNTER_TYPE.PHONE_NUMBER, true).value;
  }

  /**
   * @return {?Date}
   */
  get validFromDate() {
    const date = this._validForObject?.startDateTime;
    if (!date) {
      return null;
    }
    return new Date(date);
  }

  /**
   * @return {?Date}
   */
  get validUntilDate() {
    const date = this._validForObject?.endDateTime;
    if (!date) {
      return null;
    }
    return new Date(date);
  }

  /**
   * @return {boolean}
   */
  get isVoice() {
    return this.type === BUCKET_TYPE.VOICE;
  }

  /**
   * @return {boolean}
   */
  get isMessage() {
    return this.type === BUCKET_TYPE.MESSAGE;
  }

  /**
   * @return {boolean}
   */
  get isData() {
    return this.type === BUCKET_TYPE.DATA;
  }

  /**
   * @return {boolean}
   */
  get isMonetary() {
    return this.type === BUCKET_TYPE.MONETARY || this.type === BUCKET_TYPE.MIXED;
  }

  /**
   * @return {boolean}
   */
  get isPackage() {
    return this.type === BUCKET_TYPE.PACKAGE;
  }

  /**
   * @return {boolean}
   */
  get isSummary() {
    return /^SUM_/.test(this.id);
  }

  /**
   * @return {boolean}
   */
  get isInfinite() {
    return this.total >= MAX_VALUE;
  }

  /**
   * @return {boolean}
   */
  get isCredit() {
    return this.id === CREDIT_ID;
  }

  /**
   * @return {boolean}
   */
  get isBonusCredit() {
    return this.id === BONUS_CREDIT_ID;
  }

  /**
   * @return {boolean}
   */
  get isPreviousConsumption() {
    return PREVIOUS_CONSUMPTION_BUCKETS.includes(this.id);
  }

  /**
   * @return {boolean}
   */
  get isPreviousVoiceOsk() {
    return this.id === PREVIOUS_VOICE_OSK_ID;
  }

  /**
   * @return {boolean}
   */
  get isPreviousVoiceNonOsk() {
    return this.id === PREVIOUS_VOICE_NON_OSK_ID;
  }

  /**
   * @return {boolean}
   */
  get isPreviousVoice() {
    return this.isPreviousVoiceOsk || this.isPreviousVoiceNonOsk;
  }

  /**
   * @return {boolean}
   */
  get isPreviousSms() {
    return this.id === PREVIOUS_SMS_ID;
  }

  /**
   * @return {boolean}
   */
  get isPreviousMms() {
    return this.id === PREVIOUS_MMS_ID;
  }

  /**
   * @return {boolean}
   */
  get isPreviousMessages() {
    return this.isPreviousSms || this.isPreviousMms;
  }

  /**
   * @return {boolean}
   */
  get isPreviousData() {
    return this.id === PREVIOUS_DATA_ID;
  }

  /**
   * @return {boolean}
   */
  get isTopCalled1() {
    return this.id === PREVIOUS_TOP_CALLED_1;
  }

  /**
   * @return {boolean}
   */
  get isTopCalled2() {
    return this.id === PREVIOUS_TOP_CALLED_2;
  }

  /**
   * @return {boolean}
   */
  get isTopCalled3() {
    return this.id === PREVIOUS_TOP_CALLED_3;
  }

  /**
   * @return {boolean}
   */
  get isTopCalled() {
    return this.isTopCalled1 || this.isTopCalled2 || this.isTopCalled3;
  }

  /**
   * @return {?object}
   * @private
   */
  get _validForObject() {
    const bucketBalance = this._getBucketBalanceObject(true);
    const consumedCounter = this._getSingleCounterByType(COUNTER_TYPE.CONSUMED, false, true);

    return bucketBalance?.validFor ?? consumedCounter?.validFor ?? null;
  }

  /**
   * @param {boolean} isOptional
   * @return {?object}
   * @private
   */
  _getBucketBalanceObject(isOptional = false) {
    if (!this._data.bucketBalance || !Array.isArray(this._data.bucketBalance)) {
      if (isOptional) {
        return null;
      }
      throw new Error('Missing bucket balance array.');
    }
    if (this._data.bucketBalance.length !== 1) {
      throw new Error(
        `Exactly one bucket balance object expected, ${this._data.bucketBalance.length} found.`,
      );
    }

    return this._data.bucketBalance[0];
  }

  /**
   * Get a counter of certain type, with the assumption that there's exactly one such counter.
   * Typically, `originalSize` and `consumed` counter types should only be present once.
   *
   * @param {string} type
   * @param {boolean} isNonNumeric
   * @param {boolean} isOptional
   * @return {?object}
   * @private
   */
  _getSingleCounterByType(type, isNonNumeric = false, isOptional = false) {
    const counters = this._getCountersByType(type, isNonNumeric);
    if (counters.length === 0) {
      if (isOptional) {
        return null;
      }
      throw new Error(`Missing bucket counter of type "${type}" in bucket "${this.id}".`);
    }
    if (counters.length > 1) {
      throw new Error(
        `More than one bucket counter of type "${type}" present in bucket "${this.id}".`,
      );
    }
    return counters[0];
  }

  /**
   * Get all bucket counters of given type. Can be an empty array.
   * Validated units and values of returned bucket counters.
   *
   * @param {string} type
   * @param {boolean} isNonNumeric
   * @return {object[]}
   */
  _getCountersByType(type, isNonNumeric = false) {
    if (!this._data.bucketCounter) {
      return [];
    }

    if (!Array.isArray(this._data.bucketCounter)) {
      throw new Error(`Unexpected bucket counter format in bucket "${this.id}".`);
    }

    const counters = this._data.bucketCounter.filter(item => item.counterType === type);

    counters.forEach(counter => {
      if (isNonNumeric) {
        if (!counter.value) {
          throw new Error(
            `Missing "value" property in bucket counter of type "${type}", in bucket "${this.id}".`,
          );
        }
      } else if (typeof counter.value !== 'number') {
        if (!counter.value) {
          throw new Error(
            `Missing "value" property in bucket counter of type "${type}", in bucket "${this.id}".`,
          );
        }
        throw new Error(
          `Non-numeric "value" property in bucket counter of type "${type}", in bucket "${this.id}".`,
        );
      }

      if (counter.unit !== COUNTER_UNIT[this.type] && counter.unit !== COUNTER_UNIT.MONETARY) {
        throw new Error(
          `Unexpected unit "${counter.unit}" in bucket counter of type "${type}", in bucket "${this.id}".`,
        );
      }
    });

    return counters;
  }
}
